import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, FormControl, Switch, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { TbxTooltip } from '@/common/components'
import UpgradePlanLink from '@/common/components/UpgradePlanLink/UpgradePlanLink'

/**
 * Generates the tooltip content for customer supplied material toggle.
 *
 * @param {Object} params - Parameters for tooltip generation
 * @param {Object|null} params.selectedSheet - The currently selected sheet
 * @param {boolean} params.orgHasFeature - Whether the organization has the customer supplied material feature
 * @param {Function} params.t - Translation function
 * @returns {React.ReactElement|string} Tooltip content or empty string
 */
const getTooltipContent = ({ orgHasFeature, selectedSheet, t }) => {
    if (!selectedSheet) return ''

    if (!orgHasFeature) {
        return <UpgradePlanLink />
    }

    if (!selectedSheet.isSoldByOrganisation) {
        return t('Not for sale. Customer-supplied only.')
    }

    return ''
}

/**
 * Toggle switch for customer supplied material option.
 * Allows users to indicate if the material is supplied by the customer.
 * Memoized to prevent unnecessary re-renders when parent state changes.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.checked - Whether the toggle is checked
 * @param {boolean} [props.disabled=false] - Whether the toggle is disabled
 * @param {Object|null} props.selectedSheet - The currently selected sheet (for tooltip logic)
 * @param {boolean} props.orgHasFeature - Whether the organization has the customer supplied material feature
 * @param {Function} props.onChange - Callback when toggle changes
 * @returns {React.ReactElement} A row with label and toggle switch
 */
const CustomerSuppliedMaterialToggle = ({ checked, disabled = false, onChange, orgHasFeature, selectedSheet }) => {
    const { t } = useTranslation()

    const tooltipContent = getTooltipContent({ selectedSheet, orgHasFeature, t })

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Typography data-testid="nqp-quote-item-customer-supplied-material-label">
                {t('Customer supplied material')}
            </Typography>
            <TbxTooltip
                leaveDelay={500}
                title={tooltipContent}
                arrow
                disableFocusListener
                disableTouchListener
            >
                <FormControl
                    color="primary"
                    disabled={disabled}
                    margin="none"
                    size="small"
                    variant="outlined"
                >
                    <Switch
                        checked={checked}
                        data-testid="nqp-quote-item-customer-supplied-material"
                        onChange={(e) => onChange(e)}
                    />
                </FormControl>
            </TbxTooltip>
        </Box>
    )
}

CustomerSuppliedMaterialToggle.propTypes = {
    checked: PropTypes.bool.isRequired,
    orgHasFeature: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    selectedSheet: PropTypes.shape({
        isSoldByOrganisation: PropTypes.bool,
    }),
}

export default memo(CustomerSuppliedMaterialToggle)
